import React, { useState, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api, fmtUSD, fmtPct } from "@/lib/api";
import { PageHeader, Card, Empty } from "@/components/Bits";
import { Plus, Trash2, X, ChevronRight, Video } from "lucide-react";
import { toast } from "sonner";

const empty = {
  symbol: "", side: "long", quantity: "", entry_price: "", exit_price: "",
  entry_time: "", exit_time: "", fees: "0", notes: "", setup: ""
};

export default function Trades() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const startParam = params.get("start") || "";
  const endParam = params.get("end") || "";
  const [filterSymbol, setFilterSymbol] = useState("");
  const [filterSide, setFilterSide] = useState("");
  const [filterSetup, setFilterSetup] = useState("");
  const [filterTag, setFilterTag] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(empty);

  const { data: trades = [], isLoading } = useQuery({
    queryKey: ["trades", filterSymbol, filterSide, filterSetup, filterTag, startParam, endParam],
    queryFn: async () => {
      const q = {};
      if (filterSymbol) q.symbol = filterSymbol.toUpperCase();
      if (filterSide) q.side = filterSide;
      if (filterSetup) q.setup = filterSetup;
      if (filterTag) q.tag = filterTag;
      if (startParam) q.start = startParam;
      if (endParam) q.end = endParam;
      return (await api.get("/trades", { params: q })).data;
    },
  });

  const { data: taxonomy = { tags: [], setups: [] } } = useQuery({
    queryKey: ["taxonomy"],
    queryFn: async () => (await api.get("/taxonomy")).data,
  });

  // Fetch all user's video clips once, then build a Set of trade_ids that have replays.
  // Small, cached, only shows badge on rows w/ clips — zero perf impact.
  const { data: allClips = [] } = useQuery({
    queryKey: ["video-clips", "all"],
    queryFn: async () => (await api.get("/video/clips")).data,
  });
  const tradesWithClips = useMemo(() => {
    const s = new Set();
    (allClips || []).forEach((c) => { if (c.trade_id) s.add(c.trade_id); });
    return s;
  }, [allClips]);

  const createMut = useMutation({
    mutationFn: async (payload) => (await api.post("/trades", payload)).data,
    onSuccess: () => {
      toast.success("TRADE LOGGED");
      qc.invalidateQueries({ queryKey: ["trades"] });
      qc.invalidateQueries({ queryKey: ["stats"] });
      qc.invalidateQueries({ queryKey: ["taxonomy"] });
      setShowForm(false);
      setForm(empty);
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Failed"),
  });

  const deleteMut = useMutation({
    mutationFn: async (id) => api.delete(`/trades/${id}`),
    onSuccess: () => {
      toast.success("TRADE REMOVED");
      qc.invalidateQueries({ queryKey: ["trades"] });
      qc.invalidateQueries({ queryKey: ["stats"] });
    },
  });

  const deleteAllMut = useMutation({
    mutationFn: async () => (await api.post("/trades/delete-all")).data,
    onSuccess: (d) => {
      toast.success(`DELETED ${d.deleted} TRADES`);
      qc.invalidateQueries({ queryKey: ["trades"] });
      qc.invalidateQueries({ queryKey: ["stats"] });
      qc.invalidateQueries({ queryKey: ["taxonomy"] });
    },
  });

  const submit = (e) => {
    e.preventDefault();
    createMut.mutate({
      ...form,
      quantity: parseFloat(form.quantity),
      entry_price: parseFloat(form.entry_price),
      exit_price: parseFloat(form.exit_price),
      fees: parseFloat(form.fees || 0),
      entry_time: new Date(form.entry_time).toISOString(),
      exit_time: new Date(form.exit_time).toISOString(),
      tags: [],
    });
  };

  return (
    <div>
      <PageHeader
        eyebrow="TRADE BLOTTER"
        title="Trade Log"
        action={
          <button onClick={() => setShowForm((s) => !s)} data-testid="open-add-trade"
            className="flex items-center gap-2 px-3 py-2 text-xs bg-[color:var(--accent)] text-black hover:bg-[color:var(--accent)] transition-colors">
            {showForm ? <><X className="h-3.5 w-3.5" strokeWidth={1.5}/> CANCEL</> : <><Plus className="h-3.5 w-3.5" strokeWidth={1.5}/> LOG TRADE</>}
          </button>
        }
      />

      <div className="p-8 space-y-6">
        {showForm && (
          <Card eyebrow="NEW TRADE" title="Manual entry">
            <form onSubmit={submit} className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="manual-trade-form">
              {[
                ["symbol", "SYMBOL", "text", "AAPL"],
                ["quantity", "QTY", "number", "100"],
                ["entry_price", "ENTRY", "number", "150.50"],
                ["exit_price", "EXIT", "number", "152.10"],
                ["entry_time", "ENTRY TIME", "datetime-local", ""],
                ["exit_time", "EXIT TIME", "datetime-local", ""],
                ["fees", "FEES", "number", "0"],
                ["setup", "SETUP", "text", "breakout"],
              ].map(([k, label, type, ph]) => (
                <div key={k}>
                  <label className="label-eyebrow block mb-1">{label}</label>
                  <input required={!["fees","setup"].includes(k)} type={type} step="any"
                    value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })}
                    data-testid={`form-${k}`} placeholder={ph}
                    className="w-full bg-[color:var(--surface)] border border-[color:var(--border)] px-2 py-1.5 text-sm focus:outline-none focus:border-[color:var(--accent)] font-mono-num" />
                </div>
              ))}
              <div>
                <label className="label-eyebrow block mb-1">SIDE</label>
                <select value={form.side} onChange={(e) => setForm({ ...form, side: e.target.value })} data-testid="form-side"
                  className="w-full bg-[color:var(--surface)] border border-[color:var(--border)] px-2 py-1.5 text-sm focus:outline-none focus:border-[color:var(--accent)]">
                  <option value="long">LONG</option>
                  <option value="short">SHORT</option>
                </select>
              </div>
              <div className="col-span-2 md:col-span-3">
                <label className="label-eyebrow block mb-1">NOTES</label>
                <input type="text" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="form-notes"
                  className="w-full bg-[color:var(--surface)] border border-[color:var(--border)] px-2 py-1.5 text-sm focus:outline-none focus:border-[color:var(--accent)]" placeholder="Why this trade?" />
              </div>
              <div className="col-span-2 md:col-span-4">
                <button type="submit" disabled={createMut.isPending} data-testid="submit-trade"
                  className="w-full md:w-auto bg-white text-black px-5 py-2 text-sm hover:bg-[color:var(--accent)] transition-colors disabled:opacity-50">
                  {createMut.isPending ? "SAVING…" : "SAVE TRADE"}
                </button>
              </div>
            </form>
          </Card>
        )}

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-3 border border-[color:var(--border)] p-3">
          <span className="label-eyebrow">FILTERS</span>
          <input placeholder="SYMBOL" value={filterSymbol} onChange={(e) => setFilterSymbol(e.target.value)} data-testid="filter-symbol"
            className="bg-[color:var(--surface)] border border-[color:var(--border)] px-2 py-1 text-xs font-mono-num focus:outline-none focus:border-[color:var(--accent)]" />
          <select value={filterSide} onChange={(e) => setFilterSide(e.target.value)} data-testid="filter-side"
            className="bg-[color:var(--surface)] border border-[color:var(--border)] px-2 py-1 text-xs focus:outline-none focus:border-[color:var(--accent)]">
            <option value="">ALL SIDES</option>
            <option value="long">LONG</option>
            <option value="short">SHORT</option>
          </select>
          <select value={filterSetup} onChange={(e) => setFilterSetup(e.target.value)} data-testid="filter-setup"
            className="bg-[color:var(--surface)] border border-[color:var(--border)] px-2 py-1 text-xs focus:outline-none focus:border-[color:var(--accent)]">
            <option value="">ALL SETUPS</option>
            {taxonomy.setups.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <select value={filterTag} onChange={(e) => setFilterTag(e.target.value)} data-testid="filter-tag"
            className="bg-[color:var(--surface)] border border-[color:var(--border)] px-2 py-1 text-xs focus:outline-none focus:border-[color:var(--accent)]">
            <option value="">ALL TAGS</option>
            {taxonomy.tags.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
          {(filterSymbol || filterSide || filterSetup || filterTag || startParam || endParam) && (
            <button onClick={() => { setFilterSymbol(""); setFilterSide(""); setFilterSetup(""); setFilterTag(""); setParams({}); }}
              data-testid="clear-filters" className="text-xs text-[color:var(--accent)] hover:underline">
              CLEAR
            </button>
          )}
          {startParam && (
            <span className="text-xs font-mono-num text-[color:var(--accent)] border border-[rgba(34,197,94,0.30)] bg-[rgba(34,197,94,0.10)] px-2 py-0.5" data-testid="date-filter-pill">
              DAY: {startParam}
            </span>
          )}
          <span className="text-xs text-[color:var(--fg-3)] font-mono-num ml-auto" data-testid="trades-count">{trades.length} TRADES</span>
          {trades.length > 0 && (
            <button onClick={() => {
              if (window.confirm(`Delete ALL ${trades.length} trades? This cannot be undone.`)) deleteAllMut.mutate();
            }} data-testid="delete-all-btn"
              className="text-xs text-rose-400 hover:text-rose-300 transition-colors border border-rose-500/30 hover:border-rose-500/60 px-2 py-1">
              DELETE ALL
            </button>
          )}
        </div>

        {/* Table */}
        {isLoading ? (
          <div className="text-xs font-mono-num text-[color:var(--fg-3)]">LOADING…</div>
        ) : trades.length === 0 ? (
          <Empty title="No trades match your filters." sub="Log a trade manually or import a CSV." />
        ) : (
          <div className="border border-[color:var(--border)] overflow-x-auto">
            <table className="w-full text-xs font-mono-num">
              <thead>
                <tr className="border-b border-[color:var(--border)] text-[color:var(--fg-3)] label-eyebrow">
                  <th className="text-left p-3">DATE</th>
                  <th className="text-left p-3">SYMBOL</th>
                  <th className="text-left p-3">SIDE</th>
                  <th className="text-right p-3">QTY</th>
                  <th className="text-right p-3">ENTRY</th>
                  <th className="text-right p-3">EXIT</th>
                  <th className="text-right p-3">P&L</th>
                  <th className="text-right p-3">P&L %</th>
                  <th className="text-right p-3">HOLD</th>
                  <th className="text-left p-3">SETUP</th>
                  <th className="text-left p-3">TAGS</th>
                  <th className="p-3"></th>
                </tr>
              </thead>
              <tbody>
                {trades.map((t) => (
                  <tr key={t.id} onClick={() => navigate(`/trades/${t.id}`)}
                    className="border-b border-[color:var(--border)] hover:bg-[color:var(--surface)]/60 transition-colors cursor-pointer" data-testid={`trade-row-${t.id}`}>
                    <td className="p-3 text-[color:var(--fg-2)]">{t.exit_time?.slice(0, 16).replace("T", " ")}</td>
                    <td className="p-3 text-[color:var(--fg)] font-semibold">{t.symbol}</td>
                    <td className="p-3">
                      <span className={`px-1.5 py-0.5 text-[10px] ${t.side === "long" ? "bg-[rgba(168,85,247,0.12)] text-[color:var(--accent)]" : "bg-rose-500/10 text-rose-400"}`}>
                        {t.side.toUpperCase()}
                      </span>
                    </td>
                    <td className="p-3 text-right text-[color:var(--fg-2)]">{t.quantity}</td>
                    <td className="p-3 text-right text-[color:var(--fg-2)]">{t.entry_price.toFixed(2)}</td>
                    <td className="p-3 text-right text-[color:var(--fg-2)]">{t.exit_price.toFixed(2)}</td>
                    <td className={`p-3 text-right ${t.pnl >= 0 ? "text-[color:var(--green)]" : "text-[color:var(--loss)]"}`}>{fmtUSD(t.pnl)}</td>
                    <td className={`p-3 text-right ${t.pnl_pct >= 0 ? "text-[color:var(--green)]" : "text-[color:var(--loss)]"}`}>{fmtPct(t.pnl_pct)}</td>
                    <td className="p-3 text-right text-[color:var(--fg-3)]">{t.hold_minutes.toFixed(0)}m</td>
                    <td className="p-3 text-[color:var(--fg-3)]">{t.setup || "—"}</td>
                    <td className="p-3">
                      <div className="flex flex-wrap gap-1 max-w-[140px]">
                        {(t.tags || []).slice(0, 2).map((tag) => (
                          <span key={tag} className="text-[10px] px-1.5 py-0.5 bg-[rgba(168,85,247,0.12)] text-[color:var(--accent)] border border-[rgba(34,197,94,0.30)]">{tag}</span>
                        ))}
                        {(t.tags || []).length > 2 && <span className="text-[10px] text-[color:var(--fg-3)]">+{t.tags.length - 2}</span>}
                      </div>
                    </td>
                    <td className="p-3">
                      <div className="flex items-center gap-2 justify-end">
                        {tradesWithClips.has(t.id) && (
                          <span title="Video replay available" data-testid={`trade-has-clip-${t.id}`}>
                            <Video className="h-3.5 w-3.5" strokeWidth={1.5} style={{ color: "var(--accent)" }} />
                          </span>
                        )}
                        <button onClick={(e) => { e.stopPropagation(); deleteMut.mutate(t.id); }}
                          data-testid={`delete-trade-${t.id}`} className="text-[color:var(--fg-3)] hover:text-rose-400 transition-colors">
                          <Trash2 className="h-3.5 w-3.5" strokeWidth={1.5} />
                        </button>
                        <ChevronRight className="h-3.5 w-3.5 text-[color:var(--fg-3)]" strokeWidth={1.5} />
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
